import { RefreshCwEIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Direction, Stack } from '@prophecy/ui-v3';
import { Button } from '@prophecy/ui-v3/Button';
import { Text } from '@prophecy/ui-v3/Typography';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { pluralizeWithNumber } from '@prophecy/utils/string';
import { useMemo, useState } from 'react';

import { BranchesSummary } from './BranchesSummary';
import { BranchesTable } from './BranchesTable';
import { BranchTabProvider, useBranchTab } from './BranchTabContext';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { PartialFailureDetailModal } from './PartialFailureDetailModal';
import { PartialFailureToast } from './PartialFailureToast';
import { LocalCheckout, LocalCheckoutDeletion, LocalCheckoutDeletionResult } from './types';

type PartialFailure = {
  deletedCount: number;
  requestedCount: number;
  storageFreedBytes: number;
  failures: LocalCheckoutDeletion[];
};

function sumSizeOnDisk(checkouts: LocalCheckout[]) {
  return checkouts.reduce((sum, checkout) => sum + (checkout.detail?.sizeOnDiskBytes ?? 0), 0);
}

interface BranchesTabProps {
  projectId: string;
  projectName: string;
}

function BranchesTabContent({ projectName }: { projectName: string }) {
  const refresh = useBranchTab(({ actions }) => actions.refresh);
  const deleteCheckouts = useBranchTab(({ actions }) => actions.deleteCheckouts);
  const loading = useBranchTab(({ state }) => state.loading);
  const error = useBranchTab(({ state }) => state.error);
  const checkouts = useBranchTab(({ state }) => state.checkouts);
  const selectedIds = useBranchTab(({ state }) => state.selectedIds);

  const [pendingDeleteCheckouts, setPendingDeleteCheckouts] = useState<LocalCheckout[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [partialFailure, setPartialFailure] = useState<PartialFailure | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const selectedCheckouts = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    return checkouts.filter((checkout) => selectedSet.has(checkout.userRepositoryUid));
  }, [checkouts, selectedIds]);

  const isDeleteModalOpen = pendingDeleteCheckouts.length > 0;

  const onDeleteSelectedClick = () => {
    if (selectedCheckouts.length === 0) {
      return;
    }
    setPendingDeleteCheckouts(selectedCheckouts);
  };

  const onCloseDeleteModal = () => {
    if (deleting) {
      return;
    }
    setPendingDeleteCheckouts([]);
  };

  const handleDeleteResult = (result: LocalCheckoutDeletionResult, attemptedCheckouts: LocalCheckout[]) => {
    if (result.skipped === 0) {
      setPartialFailure(null);
      toast.success({
        content: `Deleted ${pluralizeWithNumber(result.deleted, 'branch', 'es')}`
      });
      return;
    }
    if (result.deleted === 0) {
      setPartialFailure(null);
      toast.error({
        content: `Could not delete ${pluralizeWithNumber(result.skipped, 'branch', 'es')}`
      });
      return;
    }
    const failures = result.results.filter((item) => !item.deleted);
    const failedIds = new Set(failures.map((item) => item.userRepositoryUid));
    const storageFreedBytes = sumSizeOnDisk(attemptedCheckouts.filter((c) => !failedIds.has(c.userRepositoryUid)));
    setPartialFailure({ deletedCount: result.deleted, requestedCount: result.requested, storageFreedBytes, failures });
    const partialFailureToast = toast({
      variant: 'warning',
      content: (
        <PartialFailureToast
          deletedCount={result.deleted}
          requestedCount={result.requested}
          failedCount={result.skipped}
          onSeeDetails={() => {
            partialFailureToast.close();
            setIsDetailModalOpen(true);
          }}
        />
      ),
      width: '571px',
      'data-test-id': 'partial-failure-toast'
    });
  };

  const onConfirmDelete = async () => {
    if (deleting || pendingDeleteCheckouts.length === 0) {
      return;
    }
    setDeleting(true);
    try {
      const attemptedCheckouts = pendingDeleteCheckouts;
      const result = await deleteCheckouts({
        userRepositoryUids: attemptedCheckouts.map((checkout) => checkout.userRepositoryUid)
      });
      setPendingDeleteCheckouts([]);
      handleDeleteResult(result, attemptedCheckouts);
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : 'Failed to delete branches';
      toast.error({ content: message });
    } finally {
      setDeleting(false);
    }
  };

  const onRetryFailedDeletions = async () => {
    if (!partialFailure || retrying) {
      return;
    }
    const uids = partialFailure.failures.map((failure) => failure.userRepositoryUid);
    setRetrying(true);
    try {
      const attemptedCheckouts = checkouts.filter((checkout) => uids.includes(checkout.userRepositoryUid));
      const result = await deleteCheckouts({ userRepositoryUids: uids });
      setIsDetailModalOpen(false);
      handleDeleteResult(result, attemptedCheckouts);
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : 'Failed to delete branches';
      toast.error({ content: message });
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Stack height='100%' width='100%'>
      <Stack>
        <Stack direction={Direction.horizontal} alignY='center' gap={theme.spaces.x8}>
          <Text level='xl' weight={theme.fontWeight.medium}>
            Git branches
          </Text>
          <Button
            variant='secondaryGray'
            size='xs'
            aria-label='Refresh branches'
            tooltip='Refresh'
            data-test-id='refresh-branches-button'
            onClick={() => refresh()}
            disabled={loading || deleting || retrying}
            icon={<RefreshCwEIcon type='default' spin={loading} />}
          />
        </Stack>
        <Text level='sm' tone={theme.colors.gray500}>
          Branches of this project that Prophecy keeps a copy of. Deleting a copy frees up storage and never changes
          your Git remote.
        </Text>
      </Stack>
      {error ? (
        <Text level='sm' tone={theme.colors.error500}>
          {error}
        </Text>
      ) : null}
      <BranchesSummary />
      <Stack direction={Direction.vertical} gap={theme.spaces.x16}>
        <Stack direction={Direction.horizontal} alignY='center' align='space-between'>
          <Text level='sm'>Select the branches you want to delete</Text>
          <Button
            variant='secondary'
            danger
            disabled={selectedCheckouts.length === 0}
            onClick={onDeleteSelectedClick}
            aria-label='Delete selected'
            data-test-id='delete-selected-button'
            size='s'>
            Delete selected
          </Button>
        </Stack>
        <BranchesTable />
      </Stack>
      {isDeleteModalOpen ? (
        <DeleteConfirmationModal
          open={isDeleteModalOpen}
          projectName={projectName}
          checkouts={pendingDeleteCheckouts}
          deleting={deleting}
          onClose={onCloseDeleteModal}
          onConfirm={onConfirmDelete}
        />
      ) : null}
      {partialFailure ? (
        <PartialFailureDetailModal
          open={isDetailModalOpen}
          deletedCount={partialFailure.deletedCount}
          requestedCount={partialFailure.requestedCount}
          storageFreedBytes={partialFailure.storageFreedBytes}
          failures={partialFailure.failures}
          retrying={retrying}
          onClose={() => setIsDetailModalOpen(false)}
          onRetry={onRetryFailedDeletions}
        />
      ) : null}
    </Stack>
  );
}

export function BranchesTab({ projectId, projectName }: BranchesTabProps) {
  return (
    <BranchTabProvider projectId={projectId}>
      <BranchesTabContent projectName={projectName} />
    </BranchTabProvider>
  );
}
